import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { stockBajoApi } from '../../api/consultasStock'
import SelectorArticulosModal from '../../components/SelectorArticulosModal'
import { colors, font, sx, text } from '../../design/tokens'
import { fmtFecha, fmtFechaHora } from '../../utils/fecha'
import { BotonPrimario, Card, Cargando, ErrorBox, Miniatura, Seccion } from './comunes'

// Avisar stock bajo (Martin, 23 oct 2026): depósito lee el código de barras o
// busca por SKU y avisa que queda poco. Cuántas quedan es opcional. El aviso
// solo alimenta el informe de reposición de los lunes, donde sale 3 lunes
// seguidos con su número; no marca el artículo sin stock (todavía se vende).
// El lector es el mismo de Precios ML (Safari no tiene BarcodeDetector: zxing);
// la búsqueda por nombre o parte del SKU es EL selector único de artículos.
const HINTS = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128]]])

export default function MovilStockBajo() {
  const videoRef = useRef(null)
  const controlesRef = useRef(null)
  const [escaneando, setEscaneando] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [resultado, setResultado] = useState(null)   // { codigo, articulos }
  const [elegido, setElegido] = useState(null)
  const [hecho, setHecho] = useState(null)           // el aviso recién registrado
  const [ultimos, setUltimos] = useState(null)
  const [error, setError] = useState('')
  const [selector, setSelector] = useState(false)

  const cargarUltimos = useCallback(() => {
    stockBajoApi.listar().then(({ data }) => setUltimos(data.avisos)).catch(() => setUltimos([]))
  }, [])
  useEffect(() => { cargarUltimos() }, [cargarUltimos])

  const parar = useCallback(() => {
    controlesRef.current?.stop()
    controlesRef.current = null
    setEscaneando(false)
  }, [])
  useEffect(() => parar, [parar])

  const buscar = useCallback(async (c) => {
    const limpio = String(c || '').trim()
    if (!limpio) return
    setCodigo(limpio); setBuscando(true); setError(''); setResultado(null); setElegido(null); setHecho(null)
    try {
      const { data } = await stockBajoApi.buscar(limpio)
      setResultado(data)
      if (data.articulos.length === 1) setElegido(data.articulos[0])
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo buscar el producto.')
    } finally { setBuscando(false) }
  }, [])

  const escanear = async () => {
    setError(''); setResultado(null); setElegido(null); setHecho(null); setEscaneando(true)
    window.scrollTo(0, 0)
    try {
      const lector = new BrowserMultiFormatReader(HINTS)
      controlesRef.current = await lector.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } } },
        videoRef.current,
        (res, _err, controles) => {
          if (!res) return
          controles.stop()
          controlesRef.current = null
          setEscaneando(false)
          if (navigator.vibrate) navigator.vibrate(60)
          buscar(res.getText())
        },
      )
    } catch (e) {
      setEscaneando(false)
      setError(e?.name === 'NotAllowedError'
        ? 'El navegador no dio permiso para usar la cámara. Habilitala en Ajustes → Safari → Cámara, o tipeá el código.'
        : 'No se pudo abrir la cámara. Tipeá el código o el SKU.')
    }
  }

  const otro = () => { setResultado(null); setElegido(null); setHecho(null); setCodigo(''); escanear() }

  return (
    <div>
      <div style={{ ...st.visor, display: escaneando ? 'block' : 'none' }}>
        <video ref={videoRef} style={st.video} muted playsInline />
        <div style={st.guia} />
      </div>
      {escaneando
        ? <button onClick={parar} style={st.btnSec}>Cancelar</button>
        : <BotonPrimario onClick={escanear}>📷 Escanear código de barras</BotonPrimario>}
      {!escaneando && (
        <button onClick={() => setSelector(true)} style={{ ...st.btnSec, marginTop: 10 }}>🔍 Buscar producto por nombre o SKU</button>
      )}

      <form onSubmit={(e) => { e.preventDefault(); buscar(codigo) }} style={st.manual}>
        <input value={codigo} onChange={e => setCodigo(e.target.value)} placeholder="o tipeá el código de barras o el SKU exacto" autoCapitalize="characters" autoCorrect="off" style={st.input} />
        <button type="submit" style={{ ...st.btnSec, width: 'auto', flexShrink: 0 }}>Buscar</button>
      </form>

      <ErrorBox>{error}</ErrorBox>
      {buscando && <Cargando />}

      {resultado && resultado.articulos.length === 0 && (
        <Card style={{ marginTop: 12 }}>
          <div style={st.nombre}>No hay ningún producto con «{resultado.codigo}».</div>
          <div style={st.sub}>Probá con el SKU (por ejemplo BE02057).</div>
        </Card>
      )}

      {resultado && resultado.articulos.length > 1 && !elegido && !hecho && (
        <Seccion titulo="¿Cuál de estos?">
          {resultado.articulos.map(a => (
            <button key={a.id} onClick={() => setElegido(a)} style={st.opcion}>
              <Miniatura codigo={a.codigo_alfa} />
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ ...st.nombre, display: 'block' }}>{a.descripcion}</span>
                <span style={{ ...st.sub, ...sx.mono, display: 'block' }}>{a.codigo_alfa}</span>
              </span>
              <span style={{ color: colors.textMeta, fontSize: 20 }}>›</span>
            </button>
          ))}
        </Seccion>
      )}

      {elegido && !hecho && (
        <FormAviso articulo={elegido} onHecho={(av) => { setHecho(av); cargarUltimos() }} />
      )}

      {hecho && (
        <Card style={{ marginTop: 12, borderColor: colors.ok, background: colors.okBg }}>
          <div style={{ fontFamily: font.sans, fontWeight: 700, fontSize: 16, color: colors.ok }}>✓ Aviso Nº {hecho.numero} registrado</div>
          <div style={{ ...st.sub, marginTop: 4, color: colors.text }}>{hecho.descripcion}</div>
          <div style={{ ...st.sub, marginTop: 6 }}>
            Sale en el informe de reposición de los lunes {hecho.lunes.map(fmtFecha).map(f => f.slice(0, 5)).join(', ')}.
          </div>
          <button onClick={otro} style={{ ...st.btnSec, marginTop: 12 }}>📷 Escanear otro</button>
        </Card>
      )}

      {selector && (
        <SelectorArticulosModal
          modo="simple"
          titulo="Avisar stock bajo"
          conFoto
          onSelect={(a) => { setSelector(false); buscar(a.codigo_alfa) }}
          onClose={() => setSelector(false)}
        />
      )}

      <Seccion titulo="Últimos avisos">
        {!ultimos && <Cargando />}
        {ultimos && ultimos.length === 0 && <p style={st.nota}>Todavía no hay avisos de stock bajo.</p>}
        {(ultimos || []).map(a => (
          <div key={a.numero} style={st.renglon}>
            <div style={{ minWidth: 0 }}>
              <div style={st.nombre}>{a.descripcion}</div>
              <div style={st.sub}>
                Nº {a.numero} · <span style={sx.mono}>{a.codigo_alfa}</span> · {fmtFechaHora(a.created_at)}{a.usuario ? ` · ${a.usuario}` : ''}
              </div>
              {a.nota && <div style={{ ...st.sub, fontStyle: 'italic' }}>{a.nota}</div>}
            </div>
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={st.etiqueta}>QUEDAN</div>
              <div style={st.cifra}>{a.quedan ?? '—'}</div>
            </div>
          </div>
        ))}
      </Seccion>
    </div>
  )
}

function FormAviso({ articulo, onHecho }) {
  const [quedan, setQuedan] = useState('')
  const [nota, setNota] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const sp = articulo.stock_presea

  const avisar = async () => {
    setEnviando(true); setError('')
    try {
      const { data } = await stockBajoApi.avisar({
        articulo_id: articulo.id,
        quedan: quedan.trim() === '' ? null : Number(quedan.replace(',', '.')),
        nota: nota.trim() || null,
        stock_presea: sp || null,
      })
      onHecho(data.aviso)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo registrar el aviso.')
    } finally { setEnviando(false) }
  }

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Miniatura codigo={articulo.codigo_alfa} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...st.nombre, fontWeight: 600 }}>{articulo.descripcion}</div>
          <div style={{ ...st.sub, ...sx.mono }}>{articulo.codigo_alfa}{articulo.ean ? ` · ${articulo.ean}` : ''}</div>
        </div>
      </div>

      <div style={st.datos}>
        <div>
          <div style={st.etiqueta}>STOCK PRESEA</div>
          <div style={st.cifra}>{sp ? sp.disponible : '—'}</div>
          {!sp && <div style={st.sub}>Presea no respondió</div>}
        </div>
        {articulo.sin_stock && <span style={{ ...sx.badge, background: colors.errBg, color: colors.err, alignSelf: 'center' }}>marcado sin stock</span>}
      </div>
      {articulo.ultimo_aviso && (
        <div style={{ ...st.sub, marginTop: 8 }}>
          Último aviso: Nº {articulo.ultimo_aviso.numero} · {fmtFechaHora(articulo.ultimo_aviso.created_at)}{articulo.ultimo_aviso.usuario ? ` · ${articulo.ultimo_aviso.usuario}` : ''}
        </div>
      )}

      <label style={st.label}>¿Cuántas quedan? <span style={{ color: colors.textMeta, fontWeight: 500 }}>(opcional)</span></label>
      <input value={quedan} onChange={e => setQuedan(e.target.value)} inputMode="decimal" placeholder="—" style={{ ...st.input, width: 120, flex: 'none' }} />

      <label style={st.label}>Nota <span style={{ color: colors.textMeta, fontWeight: 500 }}>(opcional)</span></label>
      <textarea value={nota} onChange={e => setNota(e.target.value)} rows={2} maxLength={500} placeholder="ej. queda una caja abierta" style={{ ...st.input, width: '100%', fontFamily: font.sans, resize: 'vertical' }} />

      <ErrorBox>{error}</ErrorBox>
      <BotonPrimario onClick={avisar} disabled={enviando} style={{ marginTop: 12 }}>
        {enviando ? 'Registrando…' : '📉 Avisar stock bajo'}
      </BotonPrimario>
    </Card>
  )
}

const mono = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }
const st = {
  visor: { position: 'relative', borderRadius: 14, overflow: 'hidden', background: colors.codeBg, marginBottom: 10 },
  video: { width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' },
  guia: { position: 'absolute', left: '12%', right: '12%', top: '45%', height: 2, background: colors.errStrong, opacity: 0.8 },
  btnSec: { padding: '13px 16px', borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', width: '100%', boxSizing: 'border-box', minHeight: 44 },
  manual: { display: 'flex', gap: 8, marginTop: 10 },
  input: { flex: 1, minWidth: 0, padding: '12px 12px', borderRadius: 12, border: `1px solid ${colors.textPlh}`, fontSize: 16, color: colors.text, background: colors.bg, outline: 'none', boxSizing: 'border-box', ...mono },
  opcion: { display: 'flex', gap: 12, alignItems: 'center', width: '100%', padding: '10px 0', minHeight: 44, border: 'none', borderBottom: `1px solid ${colors.borderSoft}`, background: 'none', cursor: 'pointer' },
  datos: { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 12, padding: '10px 12px', borderRadius: 10, background: colors.bgSoft },
  label: { display: 'block', marginTop: 14, marginBottom: 6, fontSize: 14, fontWeight: 600, color: colors.text, fontFamily: font.sans },
  renglon: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  nombre: { fontSize: text.body, color: colors.text, fontFamily: font.sans },
  sub: { fontSize: text.sec, color: colors.textSec, fontFamily: font.sans },
  etiqueta: { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: colors.textSec, fontFamily: font.sans },
  cifra: { ...mono, fontSize: 22, fontWeight: 800, color: colors.text },
  nota: { fontSize: text.sec, color: colors.textSec, margin: '6px 0 0' },
}
